import React from 'react'
import cookies from 'js-cookie'
import { ApolloQueryResult } from 'apollo-client'
import { Variation, Experiment } from '@deal/experiment-js'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { withIdentity, WithIdentityProps, IdentityConsumer } from '#src/containers/Identity'
import config from '#src/config'
import {
  MyselfQuery,
  EngagementChannelCategoryFragment,
  SellableForModalComponent,
  MyselfFragment
} from '#src/generated/graphql'
import logger from '#src/services/logger'
import { EngagementChannelCategoryAssetsComponent } from '#src/generated/graphql'
import V1 from './v1'
import V2 from './v2'

export enum Stage {
  Initial,
  Typing,
  Success
}

interface State {
  stage: Stage
  isModalOpen: boolean
  phone: string
}

interface Props extends WithAnalyticsProps {
  sellableId?: string
  categoryId: string
}

class SMSModal extends React.Component<Props & WithIdentityProps & WithAnalyticsProps, State> {
  constructor(props: Props & WithIdentityProps & WithAnalyticsProps) {
    super(props)
    this.state = {
      stage: Stage.Initial,
      isModalOpen: false,
      phone: ''
    }
  }

  public componentDidMount() {
    // If user is authenticated, only show modal if he has just registered
    if (this.props.myself) {
      if (cookies.get('sms_modal_registered')) {
        this.setIsModalOpen(true)
        this.handleStage(Stage.Success)
      }
    } else {
      this.setIsModalOpen(true)
    }
  }

  public setIsModalOpen = (isModalOpen: boolean) => {
    // On dismiss, we mark the modal viewed
    if (!isModalOpen) {
      cookies.set('sms_modal_viewed', 'true', {
        expires: 1,
        domain: config.get('consumer.clientSideCookieDomain')
      })
    }

    this.setState({ isModalOpen })
  }

  public handleStage = (stage: Stage) => {
    this.setState({ stage })
  }

  public handleChangePhone = (phone: string) => {
    this.setState({ phone })
  }

  public onDismissClick = () => {
    if (this.props.analytics) {
      this.props.analytics.smsModalDismissed()
    }

    this.setIsModalOpen(false)
  }

  public handleAuthSuccess = (category: EngagementChannelCategoryFragment) => (
    refetchIdentity?: () => Promise<ApolloQueryResult<MyselfQuery>>
  ) => {
    if (!refetchIdentity) {
      logger.captureException(
        'Could not refetch identity after auth success: "refetchIdentity" is undefined.'
      )
      return
    }

    // Set a cookie to allow cathing the modal after the refetch
    cookies.set('sms_modal_registered', 'true', {
      // Expire in 1 minute
      expires: new Date(new Date().getTime() + 1 * 60 * 1000),
      domain: config.get('consumer.clientSideCookieDomain')
    })

    refetchIdentity().then(result => {
      const myself = result.data.me
      if (myself && this.props.analytics) {
        this.props.analytics.userRegistered({
          parent_category_slug: category.slug,
          first_name: myself.user.firstName || undefined,
          last_name: myself.user.lastName || undefined
        })
      }
    })
  }

  public shouldHideModal(myself: MyselfFragment | null) {
    const modalCriteria = [
      () => {
        // Always hide the modal if they've viewed it before
        return cookies.get('sms_modal_viewed') !== undefined
      },
      () => {
        // If they're logged in and the stage isn't the success stage, hide the modal
        return myself && this.state.stage !== Stage.Success ? true : false
      },
      () => {
        // If they have a recent lead, don't continue to show them this treatment
        return myself && myself.user.mostRecentLead && myself.user.mostRecentLead.id !== undefined
          ? true
          : false
      }
    ]

    return modalCriteria.every(criteria => !!criteria())
  }

  public render() {
    const { sellableId, categoryId } = this.props
    const { stage, isModalOpen, phone } = this.state

    if (!sellableId) {
      logger.captureException('Attempted to render sms modal without a sellable id')
      return null
    }

    return (
      <IdentityConsumer>
        {({ myself }) => {
          // If you are logged in, make sure it's at the right stage so we're not unnecessrily quering
          if (myself && this.state.stage !== Stage.Success) {
          }

          if (this.shouldHideModal(myself)) {
            return null
          }

          return (
            <SellableForModalComponent
              variables={{
                friendlyIdOrId: sellableId
              }}
            >
              {({ data }) => {
                if (data && data.findSellable) {
                  const sellable = data.findSellable

                  return (
                    <EngagementChannelCategoryAssetsComponent
                      variables={{
                        categoryId
                      }}
                    >
                      {({ data }) => {
                        if (data && data.category) {
                          return (
                            <Experiment
                              id="sms-modal"
                              default="v1"
                              options={{
                                suppressTracking: myself !== undefined
                              }}
                            >
                              <Variation id="v1">
                                <V1
                                  category={data.category}
                                  isModalOpen={isModalOpen}
                                  stage={stage}
                                  phone={phone}
                                  setIsModalOpen={this.setIsModalOpen}
                                  handleStage={this.handleStage}
                                  handleAuthSuccess={this.handleAuthSuccess(data.category)}
                                  handleChangePhone={this.handleChangePhone}
                                  onDismissClick={this.onDismissClick}
                                />
                              </Variation>
                              <Variation id="v2">
                                <V2
                                  sellable={sellable}
                                  category={data.category}
                                  isModalOpen={isModalOpen}
                                  stage={stage}
                                  phone={phone}
                                  setIsModalOpen={this.setIsModalOpen}
                                  handleStage={this.handleStage}
                                  handleAuthSuccess={this.handleAuthSuccess(data.category)}
                                  handleChangePhone={this.handleChangePhone}
                                  onDismissClick={this.onDismissClick}
                                />
                              </Variation>
                            </Experiment>
                          )
                        }
                        return null
                      }}
                    </EngagementChannelCategoryAssetsComponent>
                  )
                }

                return null
              }}
            </SellableForModalComponent>
          )
        }}
      </IdentityConsumer>
    )
  }
}

export default withIdentity(withAnalytics(SMSModal))
